import React from 'react';
import { X, Ship, MapPin, Calendar, ChevronRight } from 'lucide-react';
import { ActiveVesselData, Booking, BookingBill, BookingContainer, Movement } from '../types';

interface VesselDetailsSidebarProps {
  data: ActiveVesselData | null;
  isOpen: boolean;
  onClose: () => void;
  onContainerClick: (
    container: BookingContainer,
    booking: Booking,
    bill: BookingBill,
    movements: Movement[],
  ) => void;
}

// Inline CSS folosit pentru drawer/header/cards: paleta claude_design via
// CSS variables (var(--surface), --border, --accent etc.) — același pattern
// folosit în restul paginilor migrate.
const drawerStyle = (open: boolean): React.CSSProperties => ({
  position: 'fixed',
  top: 'var(--header-h, 64px)',
  right: 0,
  bottom: 0,
  width: 400,
  maxWidth: '100vw',
  background: 'var(--surface)',
  borderLeft: '1px solid var(--border)',
  boxShadow: '-12px 0 32px rgba(0,0,0,0.12)',
  transform: open ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)',
  zIndex: 1000,
  display: 'flex',
  flexDirection: 'column',
  color: 'var(--text)',
});

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '18px 20px 16px',
  borderBottom: '1px solid var(--border)',
};

const closeBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-muted)',
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  flexShrink: 0,
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const infoCardStyle: React.CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-md, 8px)',
  padding: 14,
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
};

const valueStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  margin: 0,
};

const containerCardStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-md, 8px)',
  padding: '12px 14px',
  background: 'var(--surface)',
  cursor: 'pointer',
  transition: 'border-color 0.12s, box-shadow 0.12s, transform 0.12s',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

export const VesselDetailsSidebar: React.FC<VesselDetailsSidebarProps> = ({
  data,
  isOpen,
  onClose,
  onContainerClick,
}) => {
  if (!data) return null;

  const { vessel, containers } = data;
  const eta = vessel.etaNextPort
    ? new Date(vessel.etaNextPort).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <aside style={drawerStyle(isOpen)} aria-hidden={!isOpen}>
      <div style={headerStyle}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: 'var(--accent-soft)',
            color: 'var(--accent)',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Ship size={20} strokeWidth={2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            style={{
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {vessel.vesselName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Voiaj <span className="mono" style={{ color: 'var(--text)' }}>{vessel.voyageCode}</span>
          </div>
        </div>
        <button onClick={onClose} aria-label="Închide" style={closeBtnStyle}>
          <X size={16} />
        </button>
      </div>

      <div style={bodyStyle}>
        <div style={infoCardStyle}>
          <div>
            <div style={labelStyle}>
              <MapPin size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
              Următor port
            </div>
            <div style={valueStyle}>{vessel.nextPort || '—'}</div>
          </div>
          <div>
            <div style={labelStyle}>
              <Calendar size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
              ETA
            </div>
            <div style={valueStyle}>{eta}</div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>Locație curentă</div>
            <div style={{ ...valueStyle, fontWeight: 500 }}>{vessel.currentLocation.name}</div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '4px 0',
            borderBottom: '1px solid var(--border)',
            paddingBottom: 10,
          }}
        >
          <h3 style={sectionTitleStyle}>Marfă la bord</h3>
          <span
            style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: 999,
              padding: '2px 8px',
            }}
          >
            {containers.length}
          </span>
        </div>

        {containers.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 12px',
              color: 'var(--text-muted)',
              fontSize: 13,
            }}
          >
            Nu au fost găsite containere.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {containers.map((item, idx) => (
              <ContainerRow
                key={`${item.container.id}-${idx}`}
                item={item}
                onClick={() => onContainerClick(item.container, item.booking, item.bill, item.movements)}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

interface ContainerRowProps {
  item: ActiveVesselData['containers'][number];
  onClick: () => void;
}

const ContainerRow: React.FC<ContainerRowProps> = ({ item, onClick }) => {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...containerCardStyle,
        borderColor: hover ? 'var(--accent)' : 'var(--border)',
        boxShadow: hover ? '0 4px 12px rgba(0,0,0,0.06)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span
          className="mono"
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--accent)',
            background: 'var(--accent-soft)',
            padding: '2px 8px',
            borderRadius: 6,
          }}
        >
          {item.container.containerNumber}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            background: 'var(--text)',
            color: 'var(--surface)',
            padding: '2px 8px',
            borderRadius: 6,
          }}
        >
          {item.container.size}
          {item.container.type}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
        <Row label="Booking" value={item.booking.bookingReference} mono />
        <Row label="BL" value={item.bill.billNumber} mono />
        <Row label="Client" value={item.booking.clientName} />
        <Row
          label="Marfă"
          value={item.bill.translatedCommodity || item.bill.commodity || '—'}
          truncate
        />
      </div>
      <div
        style={{
          textAlign: 'right',
          fontSize: 11,
          color: 'var(--accent)',
          fontWeight: 600,
          opacity: hover ? 1 : 0,
          transition: 'opacity 0.12s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 4,
        }}
      >
        Vezi detalii
        <ChevronRight size={12} />
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string; mono?: boolean; truncate?: boolean }> = ({
  label,
  value,
  mono,
  truncate,
}) => (
  <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
    <span style={{ color: 'var(--text-muted)', minWidth: 56 }}>{label}</span>
    <span
      className={mono ? 'mono' : undefined}
      style={{
        color: 'var(--text)',
        fontWeight: 500,
        flex: 1,
        minWidth: 0,
        ...(truncate
          ? {
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
          : {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
      }}
    >
      {value}
    </span>
  </div>
);
